import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatDateOnly } from '../utils/timezone';
import ProcessMemberModal from '../components/ProcessMemberModal';
import AdminSetPasswordModal from '../components/AdminSetPasswordModal';

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [pendingApproval, setPendingApproval] = useState([]);
  const [verifiedUsers, setVerifiedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState('approval');
  const [processingAction, setProcessingAction] = useState(false);
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);
  const [processingUser, setProcessingUser] = useState(null);
  const [processingTrail, setProcessingTrail] = useState([]);
  const [processingTrailLoading, setProcessingTrailLoading] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [awaitingVerificationSearchQuery, setAwaitingVerificationSearchQuery] = useState('');
  const [isSetPasswordModalOpen, setIsSetPasswordModalOpen] = useState(false);
  const [setPasswordTargetUser, setSetPasswordTargetUser] = useState(null);
  const [passwordResetHistory, setPasswordResetHistory] = useState({});
  const navigate = useNavigate();

  const isBjoechia = (user?.wellis_id || '').toUpperCase() === 'WELLIS-BJOECHIA';

  // Process member on behalf of a sales-team member (WELLIS-BJOECHIA only)
  const [salesTeamMembers, setSalesTeamMembers] = useState([]);
  const [obhSalesTeamUserId, setObhSalesTeamUserId] = useState('');
  const [obhPackages, setObhPackages] = useState([]);
  const [obhFirstName, setObhFirstName] = useState('');
  const [obhLastName, setObhLastName] = useState('');
  const [obhEmail, setObhEmail] = useState('');
  const [obhPhone, setObhPhone] = useState('');
  const [obhPassword, setObhPassword] = useState('');
  const [obhReferralCode, setObhReferralCode] = useState('');
  const [obhWellisId, setObhWellisId] = useState('');
  const [obhPackageId, setObhPackageId] = useState('');
  const [obhNotes, setObhNotes] = useState('');
  const [obhSubmitting, setObhSubmitting] = useState(false);
  const [obhMessage, setObhMessage] = useState('');
  const [obhError, setObhError] = useState('');

  // Same section, second mode: process an EXISTING Approved member
  // instead of creating a new one -- search + pick from pendingApproval
  // (already fetched for the Approved tab), still funded from a chosen
  // sales-team member's balance.
  const [obhMode, setObhMode] = useState('new'); // 'new' | 'existing'
  const [obhMemberSearchQuery, setObhMemberSearchQuery] = useState('');
  const [obhSelectedTargetUserId, setObhSelectedTargetUserId] = useState('');
  const [obhExistingPackageId, setObhExistingPackageId] = useState('');
  const [obhExistingNotes, setObhExistingNotes] = useState('');
  const [obhExistingSubmitting, setObhExistingSubmitting] = useState(false);
  const [obhExistingMessage, setObhExistingMessage] = useState('');
  const [obhExistingError, setObhExistingError] = useState('');

  const formatDateGMT8 = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Singapore'
    }).format(date);
  };

  const formatTimeGMT8 = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Singapore',
      hour12: false
    }).format(date);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, navigate]);

  const fetchPasswordResetHistory = async (userId, token) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}/password-history`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.history && data.history.length > 0) {
          const timestamp = data.history[0].created_at;
          // Ensure timestamp is treated as UTC if it doesn't have timezone info
          const utcDate = timestamp.endsWith('Z') || timestamp.includes('+')
            ? new Date(timestamp)
            : new Date(timestamp + 'Z');
          setPasswordResetHistory(prev => ({
            ...prev,
            [userId]: utcDate
          }));
        }
      }
    } catch (err) {
      console.error(`Failed to fetch password history for ${userId}:`, err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch admin data');
      }

      const data = await response.json();
      // Backend returns: { pending, approved }
      // pending = users awaiting approval (legacy, now empty since all new signups are 'approved')
      // approved = approved users (may or may not be verified)
      const approved = data.approved || [];

      // Separate verified (with verified_at) from unverified approved users
      const verified = approved.filter(u => u.verified_at);
      const unverified = approved.filter(u => !u.verified_at);

      // For new design: Approved tab shows approved users (verified and unverified)
      setPendingApproval(unverified.length > 0 ? unverified : (data.pending || []));
      setVerifiedUsers(verified);

      // Fetch password reset history for each verified and unverified user
      verified.forEach(user => {
        fetchPasswordResetHistory(user.id, token);
      });
      unverified.forEach(user => {
        fetchPasswordResetHistory(user.id, token);
      });

      // Fetch processing audit trail
      fetchProcessingTrail(token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessingTrail = async (token) => {
    setProcessingTrailLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/processing-audit-trail`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch processing audit trail');
      }

      const data = await response.json();
      setProcessingTrail(data.trail || []);
    } catch (err) {
      console.error('Processing trail fetch error:', err);
    } finally {
      setProcessingTrailLoading(false);
    }
  };

  const fetchSalesTeamMembers = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/sales-team/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSalesTeamMembers(data.members || []);
      }
    } catch (err) {
      console.error('Sales-team list fetch error:', err);
    }
  };

  const fetchObhPackages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/packages`);
      if (response.ok) {
        const data = await response.json();
        setObhPackages(data.packages || []);
      }
    } catch (err) {
      console.error('Packages fetch error:', err);
    }
  };

  useEffect(() => {
    if (!isBjoechia) return;
    const token = localStorage.getItem('token');
    fetchSalesTeamMembers(token);
    fetchObhPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBjoechia]);

  const obhSelectedMember = salesTeamMembers.find((m) => m.id === obhSalesTeamUserId);
  const obhSelectedPackage = obhPackages.find((p) => p.id === obhPackageId);

  const handleProcessOnBehalf = async (e) => {
    e.preventDefault();
    setObhMessage('');
    setObhError('');

    if (!obhSalesTeamUserId) {
      setObhError('Select a sales-team member to fund this signup');
      return;
    }
    if (!obhFirstName.trim() || !obhLastName.trim()) {
      setObhError('First and last name are required');
      return;
    }
    if (!obhEmail.trim() && !obhPhone.trim()) {
      setObhError('Provide either an email or a phone number');
      return;
    }
    if (!obhPassword || obhPassword.length < 8) {
      setObhError('Password must be at least 8 characters');
      return;
    }
    if (!obhReferralCode.trim()) {
      setObhError('Sponsor referral code is required');
      return;
    }
    if (!obhPackageId) {
      setObhError('Select a package');
      return;
    }

    setObhSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/signup-internal-on-behalf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          sales_team_user_id: obhSalesTeamUserId,
          first_name: obhFirstName.trim(),
          last_name: obhLastName.trim(),
          email: obhEmail.trim() || null,
          phone: obhPhone.trim() || null,
          password: obhPassword,
          referral_code: obhReferralCode.trim(),
          wellis_id: obhWellisId.trim() || null,
          package_id: obhPackageId,
          notes: obhNotes.trim() || null,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to process member');
      }
      setObhMessage(`Member created: ${data.new_user_affiliate_code}`);
      setObhFirstName('');
      setObhLastName('');
      setObhEmail('');
      setObhPhone('');
      setObhPassword('');
      setObhReferralCode('');
      setObhWellisId('');
      setObhPackageId('');
      setObhNotes('');
      await fetchSalesTeamMembers(token);
    } catch (err) {
      setObhError(err.message || 'Failed to process member');
    } finally {
      setObhSubmitting(false);
    }
  };

  const obhFilteredApproved = pendingApproval.filter((u) => {
    const q = obhMemberSearchQuery.trim().toLowerCase();
    if (!q) return false; // require typing something -- this list can be large
    return (u.email || '').toLowerCase().includes(q)
      || (u.phone || '').toLowerCase().includes(q)
      || (u.wellis_id || '').toLowerCase().includes(q)
      || (u.affiliate_code || '').toLowerCase().includes(q)
      || (u.first_name || '').toLowerCase().includes(q)
      || (u.last_name || '').toLowerCase().includes(q);
  });
  const obhSelectedTarget = pendingApproval.find((u) => u.id === obhSelectedTargetUserId);
  const obhExistingSelectedPackage = obhPackages.find((p) => p.id === obhExistingPackageId);

  const handleProcessExistingOnBehalf = async (e) => {
    e.preventDefault();
    setObhExistingMessage('');
    setObhExistingError('');

    if (!obhSalesTeamUserId) {
      setObhExistingError('Select a sales-team member to fund this');
      return;
    }
    if (!obhSelectedTargetUserId) {
      setObhExistingError('Search for and select an approved member');
      return;
    }
    if (!obhExistingPackageId) {
      setObhExistingError('Select a package');
      return;
    }

    setObhExistingSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/process-approved-member-on-behalf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          target_user_id: obhSelectedTargetUserId,
          sales_team_user_id: obhSalesTeamUserId,
          package_id: obhExistingPackageId,
          notes: obhExistingNotes.trim() || null,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to process member');
      }
      setObhExistingMessage(data.message || 'Member processed successfully');
      setObhMemberSearchQuery('');
      setObhSelectedTargetUserId('');
      setObhExistingPackageId('');
      setObhExistingNotes('');
      await fetchSalesTeamMembers(token);
      await fetchData();
    } catch (err) {
      setObhExistingError(err.message || 'Failed to process member');
    } finally {
      setObhExistingSubmitting(false);
    }
  };

  const handleOpenProcessingModal = (user) => {
    setProcessingUser(user);
    setIsProcessingModalOpen(true);
  };

  const handleCloseProcessingModal = () => {
    setIsProcessingModalOpen(false);
    setProcessingUser(null);
  };

  const handleProcessingSuccess = () => {
    setError('');
    fetchData();
  };

  const handleRejectUser = async (userId) => {
    if (!window.confirm('Reject this user?')) return;

    setProcessingAction(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          reason: 'Rejected via admin dashboard',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to reject user');
      }

      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReverseProcessing = async (userId, memberName) => {
    if (!window.confirm(`Reverse processing for ${memberName}? This will revert their purchase status.`)) return;

    setProcessingAction(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/members/reverse-processing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to reverse processing');
      }

      alert('Processing reversed successfully');
      fetchData();
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingAction(false);
    }
  };

  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen p-margin-mobile pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
        <div className="max-w-container-max mx-auto">
          <div className="flex items-center justify-center py-12">
            <p style={{ color: '#b8a68f' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-margin-mobile pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-container-max mx-auto">
        {/* Header */}
        <div className="mb-8 border-b pb-6" style={{ borderColor: '#c9a961' }}>
          <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>Admin Dashboard</h1>
          <p className="text-body-md" style={{ color: '#b8a68f' }}>
            Manage member approvals and package confirmations
          </p>
        </div>

        {error && (
          <div className="border rounded-lg p-4 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <p style={{ color: '#c9a961' }}>{error}</p>
          </div>
        )}

        {/* Process Member on Behalf of Sales-Team (WELLIS-BJOECHIA only) */}
        {isBjoechia && (
          <div className="border rounded-lg p-6 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h2 className="text-headline-sm font-bold mb-3" style={{ color: '#c9a961' }}>Process Member Using a Sales-Team Member's RnR/Credit Points</h2>
            <p className="text-body-xs mb-4" style={{ color: '#b8a68f' }}>
              Pick any sales-team member below; the member's package will be funded from their Credit/RnR balance, not yours.
            </p>

            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setObhMode('new')}
                className="px-3 py-1.5 rounded-md text-body-xs font-medium transition"
                style={obhMode === 'new'
                  ? { backgroundColor: '#c9a961', color: '#0d2818' }
                  : { backgroundColor: 'transparent', color: '#b8a68f', border: '1px solid #3d5a4a' }}
              >
                New Signup
              </button>
              <button
                type="button"
                onClick={() => setObhMode('existing')}
                className="px-3 py-1.5 rounded-md text-body-xs font-medium transition"
                style={obhMode === 'existing'
                  ? { backgroundColor: '#c9a961', color: '#0d2818' }
                  : { backgroundColor: 'transparent', color: '#b8a68f', border: '1px solid #3d5a4a' }}
              >
                Process Approved Member
              </button>
            </div>

            {obhMode === 'new' && obhMessage && <p className="text-body-sm mb-3" style={{ color: '#c9a961' }}>{obhMessage}</p>}
            {obhMode === 'new' && obhError && <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{obhError}</p>}
            {obhMode === 'existing' && obhExistingMessage && <p className="text-body-sm mb-3" style={{ color: '#c9a961' }}>{obhExistingMessage}</p>}
            {obhMode === 'existing' && obhExistingError && <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{obhExistingError}</p>}

            <div className="mb-4">
              <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Sales-Team Member (funds this signup)</label>
              <select
                value={obhSalesTeamUserId}
                onChange={(e) => setObhSalesTeamUserId(e.target.value)}
                className="w-full p-2 rounded border text-body-sm"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              >
                <option value="">Select a sales-team member...</option>
                {salesTeamMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.wellis_id} — {m.first_name} {m.last_name} (Credit: {Number(m.credit_balance).toFixed(2)}, RnR: {Number(m.rnr_point_balance).toFixed(2)})
                  </option>
                ))}
              </select>
              {obhSelectedMember && (
                <p className="text-body-xs mt-1" style={{ color: '#b8a68f' }}>
                  Balance after this signup will be debited from {obhSelectedMember.wellis_id}, not from your own account.
                </p>
              )}
            </div>

            {obhMode === 'new' && (
            <form onSubmit={handleProcessOnBehalf} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>First Name</label>
                  <input type="text" value={obhFirstName} onChange={(e) => setObhFirstName(e.target.value)}
                    className="w-full p-2 rounded border text-body-sm" style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }} />
                </div>
                <div>
                  <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Last Name</label>
                  <input type="text" value={obhLastName} onChange={(e) => setObhLastName(e.target.value)}
                    className="w-full p-2 rounded border text-body-sm" style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Email</label>
                  <input type="email" value={obhEmail} onChange={(e) => setObhEmail(e.target.value)}
                    className="w-full p-2 rounded border text-body-sm" style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }} />
                </div>
                <div>
                  <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Phone (optional if email provided)</label>
                  <input type="tel" value={obhPhone} onChange={(e) => setObhPhone(e.target.value)}
                    className="w-full p-2 rounded border text-body-sm" style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }} />
                </div>
              </div>
              <div>
                <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Password</label>
                <input type="text" value={obhPassword} onChange={(e) => setObhPassword(e.target.value)} minLength={8}
                  className="w-full p-2 rounded border text-body-sm" style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Sponsor Referral Code</label>
                  <input type="text" value={obhReferralCode} onChange={(e) => setObhReferralCode(e.target.value)}
                    className="w-full p-2 rounded border text-body-sm" style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }} />
                </div>
                <div>
                  <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>New Member's Wellis ID (optional, auto-generated if blank)</label>
                  <input type="text" value={obhWellisId} onChange={(e) => setObhWellisId(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                    className="w-full p-2 rounded border text-body-sm" style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }} />
                </div>
              </div>
              <div>
                <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Package</label>
                <select
                  value={obhPackageId}
                  onChange={(e) => setObhPackageId(e.target.value)}
                  className="w-full p-2 rounded border text-body-sm"
                  style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                >
                  <option value="">Select a package...</option>
                  {obhPackages.map((p) => (
                    <option key={p.id} value={p.id}>{p.name_en || p.code} — {Number(p.price).toFixed(0)}</option>
                  ))}
                </select>
                {obhSelectedMember && obhSelectedPackage && (
                  <p className="text-body-xs mt-1" style={{ color: Number(obhSelectedMember.credit_balance) + Number(obhSelectedMember.rnr_point_balance) < Number(obhSelectedPackage.price) ? '#e05252' : '#b8a68f' }}>
                    {obhSelectedMember.wellis_id}'s combined balance: {(Number(obhSelectedMember.credit_balance) + Number(obhSelectedMember.rnr_point_balance)).toFixed(2)} (package price: {Number(obhSelectedPackage.price).toFixed(2)})
                  </p>
                )}
              </div>
              <div>
                <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Notes (optional)</label>
                <textarea value={obhNotes} onChange={(e) => setObhNotes(e.target.value)} rows="2"
                  className="w-full p-2 rounded border text-body-sm" style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }} />
              </div>
              <button
                type="submit"
                disabled={obhSubmitting}
                className="w-full px-4 py-3 rounded-lg font-medium disabled:opacity-50 text-black"
                style={{ backgroundColor: '#c9a961' }}
              >
                {obhSubmitting ? 'Processing...' : 'Process Member & Activate Package'}
              </button>
            </form>
            )}

            {obhMode === 'existing' && (
            <form onSubmit={handleProcessExistingOnBehalf} className="space-y-3">
              <div>
                <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Search Approved Members</label>
                <input
                  type="text"
                  value={obhMemberSearchQuery}
                  onChange={(e) => { setObhMemberSearchQuery(e.target.value); setObhSelectedTargetUserId(''); }}
                  placeholder="Search by Wellis ID, name, email, or phone..."
                  className="w-full p-2 rounded border text-body-sm"
                  style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                />
                {obhMemberSearchQuery.trim() && (
                  <div className="mt-2 max-h-56 overflow-y-auto space-y-1 border rounded p-2" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
                    {obhFilteredApproved.length === 0 ? (
                      <p className="text-body-xs" style={{ color: '#999999' }}>No matches.</p>
                    ) : (
                      obhFilteredApproved.map((u) => (
                        <button
                          type="button"
                          key={u.id}
                          onClick={() => { setObhSelectedTargetUserId(u.id); setObhMemberSearchQuery(u.wellis_id || u.affiliate_code || ''); }}
                          className="w-full text-left px-2 py-1.5 rounded text-body-xs transition"
                          style={obhSelectedTargetUserId === u.id
                            ? { backgroundColor: '#c9a961', color: '#0d2818' }
                            : { backgroundColor: 'transparent', color: '#b8a68f' }}
                        >
                          {u.wellis_id || u.affiliate_code} — {u.first_name} {u.last_name} {u.email ? `(${u.email})` : ''}
                        </button>
                      ))
                    )}
                  </div>
                )}
                {obhSelectedTarget && (
                  <p className="text-body-xs mt-1" style={{ color: '#c9a961' }}>
                    Selected: {obhSelectedTarget.wellis_id || obhSelectedTarget.affiliate_code} — {obhSelectedTarget.first_name} {obhSelectedTarget.last_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Package</label>
                <select
                  value={obhExistingPackageId}
                  onChange={(e) => setObhExistingPackageId(e.target.value)}
                  className="w-full p-2 rounded border text-body-sm"
                  style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                >
                  <option value="">Select a package...</option>
                  {obhPackages.map((p) => (
                    <option key={p.id} value={p.id}>{p.name_en || p.code} — {Number(p.price).toFixed(0)}</option>
                  ))}
                </select>
                {obhSelectedMember && obhExistingSelectedPackage && (
                  <p className="text-body-xs mt-1" style={{ color: Number(obhSelectedMember.credit_balance) + Number(obhSelectedMember.rnr_point_balance) < Number(obhExistingSelectedPackage.price) ? '#e05252' : '#b8a68f' }}>
                    {obhSelectedMember.wellis_id}'s combined balance: {(Number(obhSelectedMember.credit_balance) + Number(obhSelectedMember.rnr_point_balance)).toFixed(2)} (package price: {Number(obhExistingSelectedPackage.price).toFixed(2)})
                  </p>
                )}
              </div>

              <div>
                <label className="block text-body-xs mb-1" style={{ color: '#b8a68f' }}>Notes (optional)</label>
                <textarea value={obhExistingNotes} onChange={(e) => setObhExistingNotes(e.target.value)} rows="2"
                  className="w-full p-2 rounded border text-body-sm" style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }} />
              </div>

              <button
                type="submit"
                disabled={obhExistingSubmitting}
                className="w-full px-4 py-3 rounded-lg font-medium disabled:opacity-50 text-black"
                style={{ backgroundColor: '#c9a961' }}
              >
                {obhExistingSubmitting ? 'Processing...' : 'Process Member & Activate Package'}
              </button>
            </form>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b overflow-x-auto" style={{ borderColor: '#c9a961' }}>
          <button
            onClick={() => setSelectedTab('approval')}
            className="px-6 py-3 font-medium transition-colors whitespace-nowrap"
            style={{
              color: selectedTab === 'approval' ? '#c9a961' : '#b8a68f',
              borderBottom: selectedTab === 'approval' ? '2px solid #c9a961' : 'none',
            }}
          >
            Approved ({pendingApproval.length})
          </button>
          <button
            onClick={() => setSelectedTab('verified')}
            className="px-6 py-3 font-medium transition-colors whitespace-nowrap"
            style={{
              color: selectedTab === 'verified' ? '#c9a961' : '#b8a68f',
              borderBottom: selectedTab === 'verified' ? '2px solid #c9a961' : 'none',
            }}
          >
            Verified ({verifiedUsers.length})
          </button>
          <button
            onClick={() => setSelectedTab('processing_trail')}
            className="px-6 py-3 font-medium transition-colors whitespace-nowrap"
            style={{
              color: selectedTab === 'processing_trail' ? '#c9a961' : '#b8a68f',
              borderBottom: selectedTab === 'processing_trail' ? '2px solid #c9a961' : 'none',
            }}
          >
            Processing Trail ({processingTrail.length})
          </button>
        </div>

        {/* Approved Tab */}
        {selectedTab === 'approval' && (
          <div>
            {/* Search Bar */}
            <div className="mb-6">
              <input
                type="text"
                value={awaitingVerificationSearchQuery}
                onChange={(e) => setAwaitingVerificationSearchQuery(e.target.value)}
                placeholder="Search by email, phone, or Wellis ID"
                className="w-full px-4 py-2 rounded-lg border text-body-sm focus:outline-none"
                style={{
                  backgroundColor: '#0d2818',
                  borderColor: '#c9a961',
                  color: '#c9a961',
                }}
              />
            </div>

            {pendingApproval.length === 0 ? (
              <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <p style={{ color: '#b8a68f' }}>No approved users</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const filteredUsers = pendingApproval.filter(u => {
                    const q = awaitingVerificationSearchQuery.trim().toLowerCase();
                    if (!q) return true;
                    return (u.email || '').toLowerCase().includes(q)
                      || (u.phone || '').toLowerCase().includes(q)
                      || (u.wellis_id || '').toLowerCase().includes(q)
                      || (u.affiliate_code || '').toLowerCase().includes(q);
                  });

                  if (filteredUsers.length === 0 && awaitingVerificationSearchQuery) {
                    return (
                      <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                        <p style={{ color: '#b8a68f' }}>No users match your search</p>
                      </div>
                    );
                  }

                  return filteredUsers.map((user) => {
                  const hasPurchase = user.package_id !== null && user.package_activated_at !== null;
                  const isPreProcessed = user.processed_by_wellis_id && !user.package_id && !user.verified_at;
                  return (
                    <div key={user.id} className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-4">
                        {/* Member Info */}
                        <div>
                          <p className="text-body-sm font-medium mb-3" style={{ color: '#b8a68f' }}>Member Info</p>
                          <div className="space-y-2">
                            <div>
                              <p className="text-body-xs" style={{ color: '#999999' }}>Wellis ID</p>
                              <p className="text-body-sm font-medium" style={{ color: '#c9a961' }}>{user.wellis_id || user.affiliate_code || '—'}</p>
                            </div>
                            <div>
                              <p className="text-body-xs" style={{ color: '#999999' }}>Name</p>
                              <p className="text-body-sm font-medium" style={{ color: '#c9a961' }}>{user.first_name} {user.last_name}</p>
                            </div>
                            <div>
                              <p className="text-body-xs" style={{ color: '#999999' }}>Email</p>
                              <p className="text-body-sm" style={{ color: '#c9a961' }}>{user.email}</p>
                            </div>
                            <div>
                              <p className="text-body-xs" style={{ color: '#999999' }}>Phone</p>
                              <p className="text-body-sm" style={{ color: '#c9a961' }}>{user.phone || '—'}</p>
                            </div>
                            <div>
                              <p className="text-body-xs" style={{ color: '#999999' }}>Date Joined</p>
                              <p className="text-body-sm" style={{ color: '#c9a961' }}>{user.created_at ? formatDateOnly(user.created_at) : 'N/A'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Referrer Info */}
                        <div>
                          <p className="text-body-sm font-medium mb-3" style={{ color: '#b8a68f' }}>Referrer</p>
                          <div className="space-y-2">
                            <div>
                              <p className="text-body-xs" style={{ color: '#999999' }}>Wellis ID</p>
                              <p className="text-body-sm font-medium" style={{ color: '#c9a961' }}>{user.affiliate_code || '—'}</p>
                            </div>
                            <div>
                              <p className="text-body-xs" style={{ color: '#999999' }}>Name</p>
                              <p className="text-body-sm" style={{ color: '#c9a961' }}>{user.referrer_name || 'None'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Purchase Info */}
                        <div>
                          <p className="text-body-sm font-medium mb-3" style={{ color: '#b8a68f' }}>Purchase Info</p>
                          <div className="space-y-2">
                            <div>
                              <p className="text-body-xs" style={{ color: '#999999' }}>Type</p>
                              <p className="text-body-sm" style={{ color: '#c9a961' }}>
                                {isPreProcessed
                                  ? `Pre-Processed · Last by ${user.processed_by_wellis_id || 'Unknown'}`
                                  : hasPurchase
                                  ? `Processed by Admin (${user.processed_by_wellis_id || 'Unknown'})`
                                  : user.pending_order
                                  ? 'Checkout Started (Not Completed)'
                                  : 'Awaiting Purchase'}
                              </p>
                            </div>
                            {hasPurchase && (
                              <>
                                <div>
                                  <p className="text-body-xs" style={{ color: '#999999' }}>Package Code</p>
                                  <p className="text-body-sm" style={{ color: '#c9a961' }}>{user.package_code || '—'}</p>
                                </div>
                                <div>
                                  <p className="text-body-xs" style={{ color: '#999999' }}>Package Name</p>
                                  <p className="text-body-sm" style={{ color: '#c9a961' }}>{user.package_name_en || '—'}</p>
                                </div>
                                <div>
                                  <p className="text-body-xs" style={{ color: '#999999' }}>Value (Credit Points / SP)</p>
                                  <p className="text-body-sm" style={{ color: '#c9a961' }}>{user.package_price ? user.package_price.toLocaleString() : '—'}</p>
                                </div>
                              </>
                            )}
                            {/* A package_orders row still sitting at status='pending' --
                                usually a Stripe Checkout session the member started but
                                never completed (payment abandoned, or completed but the
                                webhook never confirmed it). Surfaced so an admin can tell
                                this apart from a member who never attempted checkout at
                                all, and trace the exact session if needed. */}
                            {!hasPurchase && user.pending_order && (
                              <>
                                <div>
                                  <p className="text-body-xs" style={{ color: '#999999' }}>Package (Pending)</p>
                                  <p className="text-body-sm" style={{ color: '#c9a961' }}>
                                    {user.pending_order.package_name_en || user.pending_order.package_code || '—'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-body-xs" style={{ color: '#999999' }}>Amount</p>
                                  <p className="text-body-sm" style={{ color: '#c9a961' }}>
                                    {user.pending_order.package_price ? user.pending_order.package_price.toLocaleString() : '—'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-body-xs" style={{ color: '#999999' }}>Checkout Started</p>
                                  <p className="text-body-sm" style={{ color: '#c9a961' }}>{formatDateOnly(user.pending_order.created_at)}</p>
                                </div>
                                {user.pending_order.stripe_checkout_session_id && (
                                  <div>
                                    <p className="text-body-xs" style={{ color: '#999999' }}>Stripe Session</p>
                                    <p className="text-body-xs font-mono break-all" style={{ color: '#c9a961' }}>
                                      {user.pending_order.stripe_checkout_session_id}
                                    </p>
                                  </div>
                                )}
                              </>
                            )}
                            {user.processed_by_wellis_id && user.pre_processed_credit_amount != null && (
                              <div>
                                <p className="text-body-xs" style={{ color: '#999999' }}>Credit Points Awarded</p>
                                <p className="text-body-sm" style={{ color: '#c9a961' }}>{user.pre_processed_credit_amount.toLocaleString()}</p>
                              </div>
                            )}
                            <div>
                              <p className="text-body-xs" style={{ color: '#999999' }}>Date Processed</p>
                              <p className="text-body-sm" style={{ color: '#c9a961' }}>
                                {hasPurchase ? formatDateOnly(user.package_activated_at) : (user.pre_processed_at ? formatDateOnly(user.pre_processed_at) : '—')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2 flex-wrap mb-4">
                        <button
                          onClick={() => handleOpenProcessingModal(user)}
                          disabled={processingAction}
                          className="flex-1 min-w-[140px] px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
                          style={{ backgroundColor: '#c9a961' }}
                          title="Open member processing dialog"
                        >
                          ⚙️ Process Member
                        </button>
                        <button
                          onClick={() => handleRejectUser(user.id)}
                          disabled={processingAction}
                          className="flex-1 min-w-[100px] px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity border disabled:opacity-50"
                          style={{ borderColor: '#c9a961', color: '#c9a961' }}
                        >
                          ✗ Reject
                        </button>
                        {hasPurchase && (
                          <button
                            onClick={() => handleReverseProcessing(user.id, `${user.first_name} ${user.last_name}`)}
                            disabled={processingAction}
                            className="flex-1 min-w-[140px] px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity border disabled:opacity-50"
                            style={{ borderColor: '#ff6b6b', color: '#ff6b6b' }}
                            title="Reverse the processing/purchase status"
                          >
                            ↶ Reverse
                          </button>
                        )}
                      </div>

                      <div className="pt-4 border-t" style={{ borderColor: '#c9a961' }}>
                        <div className="flex items-center gap-4">
                          {passwordResetHistory[user.id] ? (
                            <div className="text-body-sm" style={{ color: '#b8a68f' }}>
                              <p>Last password reset:</p>
                              <p style={{ color: '#c9a961' }}>
                                {passwordResetHistory[user.id].toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}
                              </p>
                            </div>
                          ) : (
                            <div className="text-body-sm" style={{ color: '#b8a68f' }}>
                              No password resets
                            </div>
                          )}
                          <button
                            onClick={() => {
                              setSetPasswordTargetUser(user);
                              setIsSetPasswordModalOpen(true);
                            }}
                            className="px-4 py-2 rounded font-medium text-body-sm transition-colors"
                            style={{
                              backgroundColor: '#c9a961',
                              color: '#0d2818',
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.target.style.opacity = '1'}
                          >
                            Set Password
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                  });
                })()}
              </div>
            )}
          </div>
        )}

        {/* Verified Tab */}
        {selectedTab === 'verified' && (
          <div>
            {/* Search Bar */}
            <div className="mb-6">
              <input
                type="text"
                value={memberSearchQuery}
                onChange={(e) => setMemberSearchQuery(e.target.value)}
                placeholder="Search by email, phone, or Wellis ID"
                className="w-full px-4 py-2 rounded-lg border text-body-sm focus:outline-none"
                style={{
                  backgroundColor: '#0d2818',
                  borderColor: '#c9a961',
                  color: '#c9a961',
                }}
              />
            </div>

            {verifiedUsers.length === 0 ? (
              <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <p style={{ color: '#b8a68f' }}>No verified members yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const filteredUsers = verifiedUsers.filter(m => {
                    const q = memberSearchQuery.trim().toLowerCase();
                    if (!q) return true;
                    return (m.email || '').toLowerCase().includes(q)
                      || (m.phone || '').toLowerCase().includes(q)
                      || (m.wellis_id || '').toLowerCase().includes(q);
                  });

                  if (filteredUsers.length === 0 && memberSearchQuery) {
                    return (
                      <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                        <p style={{ color: '#b8a68f' }}>No members match your search</p>
                      </div>
                    );
                  }

                  return filteredUsers.map((member) => {
                    const isAdminVerified = member.package_id === null && member.verified_at;
                    return (
                      <div key={member.id} className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-4">
                          {/* Member Info */}
                          <div>
                            <p className="text-body-sm font-medium mb-3" style={{ color: '#b8a68f' }}>Member Info</p>
                            <div className="space-y-2">
                              <div>
                                <p className="text-body-xs" style={{ color: '#999999' }}>Wellis ID</p>
                                <p className="text-body-sm font-medium" style={{ color: '#c9a961' }}>{member.wellis_id || '—'}</p>
                              </div>
                              <div>
                                <p className="text-body-xs" style={{ color: '#999999' }}>Name</p>
                                <p className="text-body-sm font-medium" style={{ color: '#c9a961' }}>
                                  {member.first_name} {member.last_name}
                                  {isAdminVerified && <span style={{ color: '#c9a961', marginLeft: '4px' }}>*</span>}
                                </p>
                              </div>
                              <div>
                                <p className="text-body-xs" style={{ color: '#999999' }}>Email</p>
                                <p className="text-body-sm" style={{ color: '#c9a961' }}>{member.email}</p>
                              </div>
                              <div>
                                <p className="text-body-xs" style={{ color: '#999999' }}>Phone</p>
                                <p className="text-body-sm" style={{ color: '#c9a961' }}>{member.phone || '—'}</p>
                              </div>
                              <div>
                                <p className="text-body-xs" style={{ color: '#999999' }}>Date Joined</p>
                                <p className="text-body-sm" style={{ color: '#c9a961' }}>{member.created_at ? formatDateOnly(member.created_at) : 'N/A'}</p>
                              </div>
                              {isAdminVerified && (
                                <p className="text-body-xs mt-2" style={{ color: '#8b7355' }}>
                                  * Verified by admin (no purchase)
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Referrer Info */}
                          <div>
                            <p className="text-body-sm font-medium mb-3" style={{ color: '#b8a68f' }}>Referrer</p>
                            <div className="space-y-2">
                              <div>
                                <p className="text-body-xs" style={{ color: '#999999' }}>Wellis ID</p>
                                <p className="text-body-sm font-medium" style={{ color: '#c9a961' }}>{member.affiliate_code || '—'}</p>
                              </div>
                              <div>
                                <p className="text-body-xs" style={{ color: '#999999' }}>Name</p>
                                <p className="text-body-sm" style={{ color: '#c9a961' }}>{member.referrer_name || 'None'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Purchase Info */}
                          <div>
                            <p className="text-body-sm font-medium mb-3" style={{ color: '#b8a68f' }}>Purchase Info</p>
                            <div className="space-y-2">
                              <div>
                                <p className="text-body-xs" style={{ color: '#999999' }}>Type</p>
                                <p className="text-body-sm" style={{ color: '#c9a961' }}>
                                  {isAdminVerified
                                    ? `Admin Approved${member.processed_by_wellis_id ? ` · Pre-Processed by ${member.processed_by_wellis_id}` : ''}`
                                    : (member.package_id
                                      ? `Processed by Admin (${member.processed_by_wellis_id || 'Unknown'})`
                                      : '—')}
                                </p>
                              </div>
                              {member.package_id && (
                                <>
                                  <div>
                                    <p className="text-body-xs" style={{ color: '#999999' }}>Package Code</p>
                                    <p className="text-body-sm" style={{ color: '#c9a961' }}>{member.package_code || '—'}</p>
                                  </div>
                                  <div>
                                    <p className="text-body-xs" style={{ color: '#999999' }}>Package Name</p>
                                    <p className="text-body-sm" style={{ color: '#c9a961' }}>{member.package_name_en || '—'}</p>
                                  </div>
                                  <div>
                                    <p className="text-body-xs" style={{ color: '#999999' }}>Value (Credit Points / SP)</p>
                                    <p className="text-body-sm" style={{ color: '#c9a961' }}>{member.package_price ? member.package_price.toLocaleString() : '—'}</p>
                                  </div>
                                </>
                              )}
                              {/* See the pending-users block above for why this is
                                  surfaced: a package_orders row still at
                                  status='pending' (e.g. an abandoned/unconfirmed
                                  Stripe Checkout) that would otherwise be invisible
                                  here. */}
                              {!member.package_id && member.pending_order && (
                                <>
                                  <div>
                                    <p className="text-body-xs" style={{ color: '#999999' }}>Package (Pending)</p>
                                    <p className="text-body-sm" style={{ color: '#c9a961' }}>
                                      {member.pending_order.package_name_en || member.pending_order.package_code || '—'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-body-xs" style={{ color: '#999999' }}>Amount</p>
                                    <p className="text-body-sm" style={{ color: '#c9a961' }}>
                                      {member.pending_order.package_price ? member.pending_order.package_price.toLocaleString() : '—'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-body-xs" style={{ color: '#999999' }}>Checkout Started</p>
                                    <p className="text-body-sm" style={{ color: '#c9a961' }}>{formatDateOnly(member.pending_order.created_at)}</p>
                                  </div>
                                  {member.pending_order.stripe_checkout_session_id && (
                                    <div>
                                      <p className="text-body-xs" style={{ color: '#999999' }}>Stripe Session</p>
                                      <p className="text-body-xs font-mono break-all" style={{ color: '#c9a961' }}>
                                        {member.pending_order.stripe_checkout_session_id}
                                      </p>
                                    </div>
                                  )}
                                </>
                              )}
                              {member.processed_by_wellis_id && member.pre_processed_credit_amount != null && (
                                <div>
                                  <p className="text-body-xs" style={{ color: '#999999' }}>Credit Points Awarded</p>
                                  <p className="text-body-sm" style={{ color: '#c9a961' }}>{member.pre_processed_credit_amount.toLocaleString()}</p>
                                </div>
                              )}
                              <div>
                                <p className="text-body-xs" style={{ color: '#999999' }}>Date Processed</p>
                                <p className="text-body-sm" style={{ color: '#c9a961' }}>
                                  {member.package_activated_at ? formatDateOnly(member.package_activated_at) : (member.pre_processed_at ? formatDateOnly(member.pre_processed_at) : (member.verified_at ? formatDateOnly(member.verified_at) : '—'))}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex items-center gap-4">
                          {passwordResetHistory[member.id] ? (
                            <div className="text-body-sm" style={{ color: '#b8a68f' }}>
                              <p>Last password reset:</p>
                              <p style={{ color: '#c9a961' }}>
                                {passwordResetHistory[member.id].toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}
                              </p>
                            </div>
                          ) : (
                            <div className="text-body-sm" style={{ color: '#b8a68f' }}>
                              No password resets
                            </div>
                          )}
                          <button
                            onClick={() => {
                              setSetPasswordTargetUser(member);
                              setIsSetPasswordModalOpen(true);
                            }}
                            className="px-4 py-2 rounded font-medium text-body-sm transition-colors"
                            style={{
                              backgroundColor: '#c9a961',
                              color: '#0d2818',
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.target.style.opacity = '1'}
                          >
                            Set Password
                          </button>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        )}

        {/* Processing Trail Tab */}
        {selectedTab === 'processing_trail' && (
          <div>
            {processingTrailLoading ? (
              <div className="text-center py-8">
                <p style={{ color: '#b8a68f' }}>Loading processing audit trail...</p>
              </div>
            ) : processingTrail.length === 0 ? (
              <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <p style={{ color: '#b8a68f' }}>No member processing recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {processingTrail.map((entry) => {
                  const processingType = entry.details?.processing_type;
                  const isSalesTeamSignup = entry.action === 'signup_internal';
                  const badgeColor = isSalesTeamSignup ? '#a78bfa' : processingType === 'live' ? '#7bc76f' : processingType === 'open_slot' ? '#999' : processingType === 'backfilled' ? '#5ba3d0' : '#f59e0b';
                  const badge = isSalesTeamSignup ? '🏷️' : processingType === 'live' ? '🟢' : processingType === 'open_slot' ? '⚪' : processingType === 'backfilled' ? '🔵' : '🟡';
                  const typeLabel = isSalesTeamSignup
                    ? `Sales Team - PreProcessed by ${entry.admin_name && entry.admin_name !== 'Unknown' ? entry.admin_name : entry.admin_email}`
                    : (processingType === 'open_slot' ? 'Open Slot' : processingType === 'pre_processed' ? 'Pre-Processed' : processingType);

                  return (
                    <div key={entry.id} className="border rounded-lg p-4" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-body-sm" style={{ color: '#b8a68f' }}>Member Processed</p>
                          <p className="font-medium" style={{ color: '#c9a961' }}>
                            {entry.user_name}
                          </p>
                          <p className="text-body-sm" style={{ color: '#b8a68f' }}>
                            {entry.user_email}
                          </p>
                        </div>
                        <div>
                          <p className="text-body-sm" style={{ color: '#b8a68f' }}>Processing Type</p>
                          <div className="inline-flex items-center gap-2 mt-1">
                            <span className="text-xl">{badge}</span>
                            <p className="font-medium capitalize" style={{ color: badgeColor }}>
                              {typeLabel}
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-body-sm" style={{ color: '#b8a68f' }}>Date/Time</p>
                          <p className="font-medium" style={{ color: '#c9a961' }}>
                            {formatDateGMT8(entry.created_at)}
                          </p>
                          <p className="text-body-sm" style={{ color: '#b8a68f' }}>
                            {formatTimeGMT8(entry.created_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-body-sm" style={{ color: '#b8a68f' }}>Admin / Package</p>
                          <p className="text-body-sm" style={{ color: '#c9a961' }}>
                            Admin: {entry.admin_email || 'Unknown'}
                          </p>
                          <p className="text-body-sm" style={{ color: '#c9a961' }}>
                            Package: {entry.details?.package_name || entry.details?.package_code || 'N/A'} {entry.details?.package_code && `(${entry.details.package_code})`}
                          </p>
                          {(entry.details?.deduction_amount || entry.details?.amount_spent) && (
                            <p className="text-body-sm" style={{ color: '#c9a961' }}>
                              {isSalesTeamSignup ? 'Credit Points Spent' : 'Deduction'}: {isSalesTeamSignup ? '' : 'SGD $'}{entry.details.deduction_amount || entry.details.amount_spent}
                            </p>
                          )}
                          {entry.details?.credit_amount && (
                            <p className="text-body-sm" style={{ color: '#c9a961' }}>
                              Credits Granted: {entry.details.credit_amount}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Process Member Modal */}
        {isProcessingModalOpen && (
          <ProcessMemberModal
            user={processingUser}
            onClose={handleCloseProcessingModal}
            onSuccess={handleProcessingSuccess}
          />
        )}

        {isSetPasswordModalOpen && (
          <AdminSetPasswordModal
            user={setPasswordTargetUser}
            onClose={() => setIsSetPasswordModalOpen(false)}
            onSuccess={() => {
              setIsSetPasswordModalOpen(false);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
}
